import React from 'react';
import { Dialog, Stack } from '@mui/material';
import { ModalHeader } from 'components/ModalHeader/ModalHeader';
import { useLayoutElements } from 'contexts/LayoutElementsContext/useLayoutElements';
import { Button } from 'components/Button/Button';
import { SignupFormData } from 'features/signUp/types';
import { useSignupHelpDialog } from 'features/signUp/hooks/useSignupHelpDialog';
import { ConfirmationDialog } from 'components/ConfirmationDialog/ConfirmationDialog';
import { useTranslationContext } from 'contexts/TranslationContext/TranslationContext';
import { SignUpHelpDialogForm } from './SignUpHelpDialogForm';
import { SignUpHelpDialogConsent } from './SignUpHelpDialogConsent';
import { SignUpHelpDialogConfirmation } from './SignUpHelpDialogConfirmation';

interface SignUpHelpDialogProps {
  isHelpDialogOpen: boolean;
  onHelpDialogClose: () => void;
  signUpFormData: SignupFormData;
}

export const SignUpHelpDialog = ({
  signUpFormData,
  isHelpDialogOpen,
  onHelpDialogClose,
}: SignUpHelpDialogProps) => {
  const { isAlertOpen } = useLayoutElements();
  const modalTitleId = 'help-modal-header-title';
  const { translation } = useTranslationContext();
  const {
    currentState,
    showBackButton,
    handleClose,
    modalTitle,
    handleBackButtonClick,
    handleButtonClick,
    formControl,
    invitedByoptions,
    isSubmitting,
    showInvitedBy,
    submitButtonText,
    isValid,
    hasError,
    clearError,
  } = useSignupHelpDialog({
    signUpFormData,
    onHelpDialogClose,
  });

  return (
    <>
      <Dialog
        open={isHelpDialogOpen}
        onClose={handleClose}
        fullWidth
        aria-labelledby={modalTitleId}
        maxWidth="xs"
        className="help-dialog"
        disableEscapeKeyDown={isAlertOpen}
        aria-modal="true"
        disableEnforceFocus // Allow TalkBack to navigate freely
        PaperProps={{
          tabIndex: 0,
        }}
      >
        <ModalHeader
          showBackButton={showBackButton}
          showCloseButton
          onClickBack={handleBackButtonClick}
          onClickClose={handleClose}
          modalTitle={modalTitle}
          modalTitleProps={{ textTransform: 'capitalize', id: modalTitleId }}
        />
        <form className="help-dialog-form" aria-label="Help Dialog Form">
          <Stack spacing={4} padding={4}>
            {currentState === 'form' && (
              <SignUpHelpDialogForm
                formControl={formControl}
                invitedByoptions={invitedByoptions}
                showInvitedByField={showInvitedBy}
              />
            )}
            {currentState === 'consent' && <SignUpHelpDialogConsent />}
            {currentState === 'confirmation' && <SignUpHelpDialogConfirmation />}
            <Button
              variant="primary"
              size="xlarge"
              isLoading={isSubmitting}
              disabled={!isValid}
              onClick={handleButtonClick}
              fullWidth
              sx={{ marginTop: 'auto' }}
            >
              {submitButtonText}
            </Button>
          </Stack>
        </form>
      </Dialog>
      <ConfirmationDialog
        open={hasError}
        onClose={clearError}
        title={translation('help_request_could_not_be_submitted')}
        description={translation('already_received_this_message')}
        handleYes={handleButtonClick}
        handleNo={clearError}
        yesButtonLabel={translation('retry')}
        noButtonLabel={translation('cancel')}
      />
    </>
  );
};
